import React, { useState } from "react";
import PropTypes from "prop-types";
import { useSnackbar } from "components/GlobalSnackbar";
import { useConfirmDialog } from "components/ConfirmDialog";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  TextField,
  Grid,
} from "@mui/material";
import { buttonStyles } from "utils/buttonStyles";
import { dialogHeaderStyles, dialogContentStyles } from "utils/dialogStyles";
import { handleApiError } from "utils/errorHandler";

import { apiTambahInbois } from "api/inbois";
import { apiGetAkaunByNoAkaun } from "api/akaun";
import { apiTambahLejar, apiDeleteLejarByNoInbois } from "api/lejar";

function TambahInboisBakiAwal({ open, onClose, selectedAkaun, onSuccess }) {
  const { showSuccess, showError, showWarning } = useSnackbar();
  const { showConfirm } = useConfirmDialog();
  const [tarikh_inbois, setTarikhInbois] = useState("");
  const [jumlah, setJumlah] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  // Get user info from localStorage
  const user = (() => {
    try {
      return JSON.parse(localStorage.getItem("user") || "{}");
    } catch {
      return {};
    }
  })();

  // Reset fields to empty every time popup is opened
  React.useEffect(() => {
    if (open) {
      setTarikhInbois("");
      setJumlah("");
      setError("");
    }
  }, [open]);

  const handleSimpan = async () => {
    setError("");
    if (!tarikh_inbois || !jumlah) {
      showWarning("Sila isi semua maklumat wajib.");
      return;
    }
    setSaving(true);
    try {
      // Fetch akaun details for kadar_sewa and kategori_kutipan
      const noAkaunRef = selectedAkaun?.noakaun || selectedAkaun?.no_akaun || "";
      let akaunDetails;
      try {
        const res = await apiGetAkaunByNoAkaun(noAkaunRef);
        akaunDetails = Array.isArray(res?.data) ? res.data[0] : res?.data;
      } catch (err) {
        showError("Gagal mendapatkan maklumat akaun untuk Kadar Sewa dan Kategori Kutipan.");
        setSaving(false);
        return;
      }
      if (!akaunDetails || !akaunDetails.kadar_sewa || !akaunDetails.kategori_kutipan) {
        showError("Maklumat Kadar Sewa atau Kategori Kutipan tidak lengkap pada akaun ini.");
        setSaving(false);
        return;
      }
      const isoDate = new Date(tarikh_inbois).toISOString();
  // Today's date ISO string for tarikh_simpan
  const todayIso = new Date().toISOString();
      const payload = {
        mark_batal: 0,
        mark_delete: 0,
  tarikh_inbois: isoDate,
  tarikh_simpan: todayIso,
        tarikh_bayar: null, // set to null for Prisma DateTime compatibility
        tarikh_terakhir_jana: "", // will be updated elsewhere
        jumlah: parseFloat(jumlah),
        jumlah_total_detail: 0.0,
        perkara: "Baki Awal Tunggakan",
        mark_post: 1,
        bahagian_pemantau: user.bahagian || "",
        id_pengguna: (user.id_pengguna || "").toLowerCase(),
        id_inbois_bahagian: 0,
        id_akaun: 0,
        no_akaun: noAkaunRef,
        no_inbois: noAkaunRef + "-BAT",
        kod_harta: akaunDetails.kodharta || akaunDetails.kod_harta || "",
        bil_hari_lewat_bayar: 0,
        notis_pertama: 0,
        status_notis_pertama: 0,
        notis_kedua: 0,
        status_notis_kedua: 0,
        notis_ketiga: 0,
        status_notis_ketiga: 0,
        the_diff: 0,
        aging_till6: 0,
        aging7_12: 0,
        aging13_18: 0,
        aging19_24: 0,
        aging25_30: 0,
        aging31_36: 0,
        aging37_72: 0,
        aging73_120: 0,
        aging_more120: 0,
        no_resit: "",
        kategori_inbois: "BAT",
        tunggakan_to_date: 0.0,
        denda_to_date: 0.0,
        baki_akhir_lejar: 0.0,
        peratus_diskaun: 0.0,
        mark_bayar: 0,
        jumlah_bayar: 0.0,
      };
      await apiTambahInbois(payload);
      // --- Call lejar API after inbois is created ---
      const lejarPayload = {
        tarikh_simpan: todayIso,
        no_akaun: noAkaunRef,
        kod_harta: akaunDetails.kodharta || akaunDetails.kod_harta || "",
        tarikh_inbois: isoDate,
        tarikh_resit: isoDate,
        doc_date: isoDate,
        no_inbois: noAkaunRef + "-BAT",
        perkara: "Baki Awal Tunggakan",
        debit: parseFloat(jumlah),
        baki: parseFloat(jumlah),
        id_pengguna: (user.id_pengguna || "").toLowerCase(),
        bahagian_pemantau: user.bahagian || "",
      };
      await apiTambahLejar(lejarPayload);
      // --- End call lejar API ---
      if (onSuccess) onSuccess();
      onClose();
    } catch (e) {
      handleApiError(e, showError);
    } finally {
      setSaving(false);
    }
  };

  const handleHapus = async () => {
    if (!selectedAkaun) return;
    // Always construct the correct reference for deletion (must match DB, e.g. 123-BAT)
    let no_inbois_original = selectedAkaun?.no_inbois;
    if (!no_inbois_original) {
      const akaunRef = selectedAkaun?.noakaun || selectedAkaun?.no_akaun || "";
      no_inbois_original = akaunRef ? akaunRef + "-BAT" : "";
    }
    if (!no_inbois_original) {
      showError("Rujukan no_inbois tidak sah untuk hapus lejar.");
      return;
    }
    console.log("Deleting lejar and inbois with reference:", no_inbois_original);
    const confirmed = await showConfirm({
      title: 'Hapus Rekod',
      message: 'Anda pasti ingin hapus rekod ini?',
      confirmText: 'Hapus',
      type: 'error'
    });
    if (!confirmed) return;
    setSaving(true);
    try {
      // Delete from lejar by no_resit (using original no_inbois)
      await apiDeleteLejarByNoInbois(no_inbois_original);
      // Delete from inbois table (implement your delete API call here if needed)
      // await apiDeleteInbois(no_inbois_original);
      if (onSuccess) onSuccess();
      onClose();
    } catch (e) {
      // Try to show backend error message if available
      let msg = "Gagal menghapus rekod lejar/inbois. Sila cuba lagi.";
      if (e && e.response && e.response.data && e.response.data.error) {
        msg = e.response.data.error;
      }
      showError(msg);
    } finally {
      setSaving(false);
    }
  };

  const handleClose = () => {
    setTarikhInbois("");
    setJumlah("");
    setError("");
    onClose();
  };
  return (
    <Dialog open={open} onClose={handleClose} maxWidth="xs" fullWidth>
      <DialogTitle sx={dialogHeaderStyles.success}>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Typography variant="h6" sx={{ color: "#fff" }}>Inbois Baki Awal</Typography>
        </Box>
      </DialogTitle>
      <DialogContent dividers sx={{ ...dialogContentStyles.light, p: 2 }}>
        <Grid container spacing={2}>
          <Grid item xs={12} md={12}>
            <TextField
              label="Tarikh Inbois"
              type="date"
              value={tarikh_inbois}
              onChange={(e) => setTarikhInbois(e.target.value)}
              InputLabelProps={{ shrink: true }}
              fullWidth
              size="small"
              required
            />
          </Grid>
          <Grid item xs={12} md={12}>
            <TextField
              label="Baki Awal Tunggakan"
              value={jumlah}
              onChange={(e) => setJumlah(e.target.value.replace(/[^\d.]/g, ""))}
              fullWidth
              size="small"
              required
            />
          </Grid>
          {/* Error is now shown as a browser alert, not inside the dialog */}
        </Grid>
      </DialogContent>
      <DialogActions sx={dialogContentStyles.default}>
        <Grid
          item
          xs={12}
          md={6}
          lg={6}
          sx={{ display: "flex", justifyContent: "flex-end", gap: 1 }}
        >
          <Button
            onClick={handleSimpan}
            variant="contained"
            disabled={saving}
            size="small"
            sx={buttonStyles.save}
          >
            SIMPAN
          </Button>
          <Button
            onClick={handleClose}
            variant="contained"
            size="small"
            sx={buttonStyles.cancel}
          >
            BATAL
          </Button>
        </Grid>
      </DialogActions>
    </Dialog>
  );
}

TambahInboisBakiAwal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  selectedAkaun: PropTypes.object,
  onSuccess: PropTypes.func,
};

export default TambahInboisBakiAwal;
